import {useAtomValue} from "jotai";
import {teamsUserCredentialAtom} from "../store.ts";
import {useMutation, UseMutationOptions} from "@tanstack/react-query";
import {useGraphClient} from "../hooks";

export function useFetchNamesMutation(
    options?: Omit<UseMutationOptions<Record<string, {name: string, email: string}>, Error, string[], unknown>, 'mutationFn'>
) {

    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);

    const {
        getGraphClient,
    } = useGraphClient(
        teamsUserCredential,
        ["User.ReadBasic.All", "Team.ReadBasic.All", "TeamMember.Read.All"]
    );

    return useMutation({
        mutationFn: async (usersIds: string[]) => {
            const graphApiClient = await getGraphClient();
            if (!graphApiClient || usersIds.length === 0) {
                return {};
            }

            const userMap = new Map<string, Promise<{name: string, email: string}>>();
            usersIds.forEach((userId: any) => {
                userMap.set(userId, graphApiClient.api(`/users/${userId}`)
                    .get()
                    .then((response) => {
                        return {name: response?.displayName, email: response?.mail};
                    })
                    .catch(() => ({name: userId, email: '-'})));
            });

            return Object.fromEntries((await resolvePromisesInBatches(userMap, 20)).entries());
        }, ...options
    });
}

async function resolvePromisesInBatches<K, V>(
    promiseMap: Map<K, Promise<V>>,
    batchSize: number
): Promise<Map<K, V>> {
    const entries = Array.from(promiseMap.entries()); // [[key, promise]]
    const resultMap = new Map<K, V>();

    for (let i = 0; i < entries.length; i += batchSize) {
        const batch = entries.slice(i, i + batchSize);

        const resolvedBatch = await Promise.all(
            batch.map(([key, promise]) =>
                promise.then(value => [key, value] as [K, V])
            )
        );

        for (const [key, value] of resolvedBatch) {
            resultMap.set(key, value);
        }
    }

    return resultMap;
}

